import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { StoreType } from "@omni/types/api/store/cloud/shared/enums/storeType.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import queryString from "query-string";
import { useCallback, useEffect, useRef, useState } from "react";

import { useCartServiceApi } from "@/api/hooks/cartService.hook";
import { DefaultFor } from "@/api/mappers/default.mapper";
import { StorePickerModal } from "@/components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@/components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@/components/Store/StorePicker/types/storePickerType.enum";
import { useThunkDispatch } from "@/redux/dispatcher";
import { addConfigurableProductToCart } from "@/redux/modules/api/data/cart/cart.module";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import { GetStoreStrategy } from "@/services/store.service";
import { StorePickerModalStates } from "@/utils/enums";
import { useStoreCode } from "@/utils/hooks/store/useStoreCode.hook";

import {
    handleMessage,
    sendFailMessage,
    sendSuccessMessage
} from "./configurableProduct.client";

const FixedFeatures = [StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE];

type Props = {
    configUrl: string;
    endPoint: string;
    configEndPoint: string;
};

type ConfigurableProductConfigurationResponse = {
    defaultDeliveryType: DeliveryType;
};

const ConfigurableProductComponent = ({
    configUrl,
    endPoint = "/configurators/timber",
    configEndPoint = "/configurators/config/timber"
}: Props) => {
    const { t } = useGranite();

    const perferredStoreCode = useStoreCode({
        strategy: GetStoreStrategy.Stock,
        startNewSession: true
    });

    const [storePickerModalState, setStorePickerModalState] = useState(
        StorePickerModalStates.Closed
    );
    const dispatch = useThunkDispatch();
    const [iframeUrl, setIframeUrl] = useState<string>();
    const [cartData, setCartData] = useState();
    const [cartMessage, setCartMessage] = useState<any>();

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [{ data }] = useCartServiceApi({
        url: configEndPoint,
        method: "GET",
        mapper: DefaultFor<ConfigurableProductConfigurationResponse>()
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleStoreSelect = useCallback(
        (
            configuratorData: any,
            origin: string,
            shop?: { code: string },
            onClose?: () => void
        ): void => {
            const addToCartPromise = dispatch(
                addConfigurableProductToCart(
                    { ...(configuratorData || {}), shopId: shop?.code || null },
                    endPoint
                )
            );

            addToCartPromise
                .then((action: TApiAction) =>
                    sendSuccessMessage(
                        configuratorData,
                        iframeRef.current?.contentWindow ?? null,
                        origin,
                        action
                    )
                )
                .catch(() =>
                    sendFailMessage(
                        configuratorData,
                        iframeRef?.current?.contentWindow ?? null,
                        origin
                    )
                )
                .finally(() => onClose?.());
        },
        [iframeRef, dispatch]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const addToCartHandler = useCallback(
        (cartData: any, origin: string, defaultDeliveryType: DeliveryType) => {
            if (
                defaultDeliveryType === DeliveryType.ClickAndCollect &&
                !isDefined(perferredStoreCode)
            ) {
                setCartData(cartData);
                setStorePickerModalState(StorePickerModalStates.AddToCart);
            } else {
                handleStoreSelect(
                    cartData,
                    origin,
                    isDefined(perferredStoreCode)
                        ? { code: perferredStoreCode }
                        : undefined
                );
            }
        },
        [
            perferredStoreCode,
            setCartData,
            setStorePickerModalState,
            handleStoreSelect
        ]
    );

    const handleOnStorePickerSubmit = useCallback(
        (store: CloudStore, onClose?: () => void) => {
            handleStoreSelect(cartData, cartMessage.origin, store, onClose);
        },
        [handleStoreSelect, cartData, cartMessage]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleOnStorePickerClose = useCallback(
        () => setStorePickerModalState(StorePickerModalStates.Closed),
        [setStorePickerModalState]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (configUrl) {
            const parsedQuery = queryString.parseUrl(configUrl);
            const parentQueryParams = queryString.parseUrl(location.search);
            const newQueryParams = {
                ...parsedQuery.query,
                ...parentQueryParams.query,
                clientHeight: document.body.clientHeight.toString()
            };

            setIframeUrl(
                queryString.stringifyUrl({
                    ...parsedQuery,
                    query: newQueryParams
                })
            );
        }
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const messageHandler = (message: any) => {
            const domain = new URL(configUrl).host;

            if (
                !message.origin.includes(domain) ||
                !iframeRef.current?.contentWindow
            ) {
                return;
            }

            setCartMessage(message);
            handleMessage(
                message,
                iframeRef?.current,
                addToCartHandler,
                domain,
                data?.defaultDeliveryType
            );
        };

        window.addEventListener("message", messageHandler);
        return () => {
            window.removeEventListener("message", messageHandler);
        };
    }, [
        configUrl,
        data?.defaultDeliveryType,
        addToCartHandler,
        handleMessage,
        setCartMessage
    ]);

    if (!iframeUrl || !endPoint) return null;

    return (
        <>
            <iframe
                className={cn("tw:border-none")}
                ref={iframeRef}
                src={iframeUrl}
                width={"100%"}
                height={"800"}
                scrolling={"no"}
            ></iframe>
            <StorePickerModal
                titleTK="configurableProduct.storepicker.title"
                subTitleTK="configurableProduct.storepicker.subTitle"
                type={StorePickerType.SELECT}
                infoLink={null}
                submitButtonText={
                    storePickerModalState === StorePickerModalStates.AddToCart
                        ? t("general.storepicker.button.confirmStoreAndOrder")
                        : ""
                }
                isOpen={storePickerModalState !== StorePickerModalStates.Closed}
                storeCode={perferredStoreCode ?? null}
                fixedFeatures={FixedFeatures}
                displayStockLevel={false}
                onSubmit={handleOnStorePickerSubmit}
                onClose={handleOnStorePickerClose}
                filterStoreType={StoreType.WalkIn}
            />
        </>
    );
};

export { ConfigurableProductComponent };
